import { theme } from '@Styles/MuiTheme';
import { useAuth0 } from '@auth0/auth0-react';
import _isNil from 'lodash/isNil';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import EntitiesApi from '@Data/entities/Api';
import { selectedOrganizationState } from '@Data/organizations/Reducer';
import ProcessesApi from '@Data/processes/Api';
import ProjectsApi from '@Data/projects/Api';
import { IProject, IUpdateProject } from '@Data/projects/Interfaces';
import { projectsState } from '@Data/projects/Reducer';
import {
    selectedTeamState,
    teamMembersState,
    teamsState,
} from '@Data/teams/Reducer';
import { getParsedAuth0Username } from '@Services/authentication/Helpers';
import { KoruRoutes } from '@Services/navigation/Paths';
import { success } from '@Services/notification/NotificationServices';
import HomeDashboard from './components/HomeDashboard/HomeDashboard';
import ManageProjectDialogue, {
    ManageProjectType,
} from './components/ManageProject/ManageProjectDialog';

const HomeView = () => {
    const [areProjectsLoading, setAreProjectsLoading] = useState<boolean>(true);
    const [userId, setUserId] = useState<string | null>(null);
    const selectedOrganization = useSelector(selectedOrganizationState);
    const selectedTeam = useSelector(selectedTeamState);
    const teamMembers = useSelector(teamMembersState);
    const [projectToEdit, setProjectToEdit] = useState<IProject>(null);
    const [manageProjectType, setManageProjectType] =
        useState<ManageProjectType>(undefined);
    const [isCreateProjectOpen, setIsCreateProjectOpen] =
        useState<boolean>(false);
    const projects: Array<IProject> = useSelector(projectsState);
    const navigate = useNavigate();
    const { user } = useAuth0();

    useEffect(() => {
        setUserId(getParsedAuth0Username(user.sub));
    }, []);

    useEffect(() => {
        if (_isNil(selectedTeam)) {
            setAreProjectsLoading(true);
        } else {
            getProjects();
        }
    }, [selectedTeam, teamMembers]);

    const getProjects = async () => {
        await ProjectsApi.getProjects(selectedTeam.team_id, () => {
            setAreProjectsLoading(false);
            setProjectToEdit(null);
        });
    };

    const viewProject = (project: IProject) => {
        ProjectsApi.setProject(project, () => {
            EntitiesApi.clearEntity();
            ProcessesApi.getUserProcessesByProject(userId, project.project_id);
            const url = KoruRoutes.qc.getUrl(
                selectedOrganization.organisation_id,
                selectedTeam.team_id,
                project.project_id
            );
            navigate(url);
        });
    };

    const handleSubmitProject = async (
        projectName: string,
        isEdit: boolean,
        onSuccess: () => void
    ) => {
        const callback = () => {
            setIsCreateProjectOpen(false);
            setAreProjectsLoading(true);
            getProjects();
            setTimeout(onSuccess, theme.transitions.duration.standard);
        };

        if (isEdit) {
            await ProjectsApi.editProject(projectToEdit, projectName);
            callback();
        } else {
            await ProjectsApi.createProject(selectedTeam.team_id, projectName);
            callback();
        }
    };

    const openEditProject = (
        project: IProject,
        manageProjectType: ManageProjectType
    ) => {
        setProjectToEdit(project);
        setManageProjectType(manageProjectType);
        setIsCreateProjectOpen(true);
    };

    const handleDeleteProject = async (
        project: IProject,
        callback?: () => void
    ) => {
        await ProjectsApi.deleteProject(project);
        setAreProjectsLoading(true);
        getProjects();
        success('Project deleted.');
        if (callback) {
            callback();
        }
    };

    const onCloseManageProject = () => {
        setIsCreateProjectOpen(false);
        setProjectToEdit(null);
        setManageProjectType(undefined);
    };

    const openCreateProject = () => {
        setManageProjectType(ManageProjectType.CREATE);
        setIsCreateProjectOpen(true);
    };

    return (
        <React.Fragment>
            <HomeDashboard
                projects={projects}
                areProjectsLoading={areProjectsLoading}
                onProjectClick={viewProject}
                onCreateProject={openCreateProject}
                onDeleteProject={handleDeleteProject}
                onEdit={openEditProject}
            />
            <ManageProjectDialogue
                isOpen={isCreateProjectOpen}
                onCancel={onCloseManageProject}
                onSubmit={handleSubmitProject}
                projectToEdit={projectToEdit}
                displayType={manageProjectType}
            />
        </React.Fragment>
    );
};

export default HomeView;
